/*
  <slider-track> - reusable scroll-snap slider with arrows and dots.

  Markup contract (any section can reuse it):

  <slider-track
    data-visible-desktop="3"   items visible above 900px  (fractions allowed, e.g. 3.5)
    data-visible-tablet="2"    items visible 750px - 900px
    data-visible-mobile="1.25" items visible up to 749px
    data-rows-mobile="2"      optional: items stacked per column on mobile (a "slide" = rows items)
  >
    <button data-slider-prev>...</button>
    <div data-slider-track>                (the scrolling element)
      <div data-slider-item>...</div>      (one per slide)
    </div>
    <button data-slider-next>...</button>
    <div data-slider-dots></div>
  </slider-track>

  Layout/width of the items is handled by the section's CSS; this file only
  drives scrolling, the arrows and the dots.
*/
if (!customElements.get('slider-track')) {
  class SliderTrack extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.dotsWrap = this.querySelector('[data-slider-dots]');
      if (!this.track) return;

      this.onResize = this.render.bind(this);
      this.onScroll = () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(this.render.bind(this), 100);
      };

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          const current = this.getIndex();
          this.goTo(current === 0 ? this.maxIndex() : current - 1);
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          const current = this.getIndex();
          this.goTo(current >= this.maxIndex() ? 0 : current + 1);
        });
      }

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.render();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      clearTimeout(this.scrollTimer);
    }

    get items() {
      return Array.from(this.track.querySelectorAll('[data-slider-item]'));
    }

    visibleItems() {
      const width = window.innerWidth;
      let value;
      if (width <= 749) value = this.dataset.visibleMobile;
      else if (width <= 900) value = this.dataset.visibleTablet;
      else value = this.dataset.visibleDesktop;
      return Math.max(1, Math.floor(parseFloat(value) || 1));
    }

    // Items stacked in one column (only used on mobile, default 1)
    rows() {
      if (window.innerWidth > 749) return 1;
      return Math.max(1, parseInt(this.dataset.rowsMobile, 10) || 1);
    }

    maxIndex() {
      const rows = this.rows();
      const slides = Math.ceil(this.items.length / rows);
      return Math.max(0, slides - this.visibleItems());
    }

    getIndex() {
      const items = this.items;
      if (!items.length) return 0;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (maxScroll > 0 && this.track.scrollLeft >= maxScroll - 2) {
        return this.maxIndex();
      }

      const first = items[0].offsetLeft;
      let closest = 0;
      let smallest = Infinity;

      items.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft - first - this.track.scrollLeft);
        if (distance < smallest) {
          smallest = distance;
          closest = index;
        }
      });

      return Math.min(Math.floor(closest / this.rows()), this.maxIndex());
    }

    goTo(index) {
      const items = this.items;
      if (!items.length) return;
      index = Math.max(0, Math.min(index, this.maxIndex()));
      this.track.scrollTo({
        left: items[index * this.rows()].offsetLeft - items[0].offsetLeft,
        behavior: 'smooth',
      });
    }

    render() {
      const canSlide = this.track.scrollWidth > this.track.clientWidth + 1;

      if (this.dotsWrap) {
        this.dotsWrap.innerHTML = '';
        if (canSlide) {
          const count = this.maxIndex() + 1;
          const current = this.getIndex();

          for (let i = 0; i < count; i++) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
            if (i === current) dot.classList.add('active');
            dot.addEventListener('click', () => this.goTo(i));
            this.dotsWrap.appendChild(dot);
          }
        }
      }

      [this.prevButton, this.nextButton].forEach((button) => {
        if (button) button.hidden = !canSlide;
      });
    }
  }

  customElements.define('slider-track', SliderTrack);
}
